/**
 * The room's less-used actions, behind "⋯" at the end of the title row.
 *
 * "Clear all items" was a red button beside the room's name: the second thing
 * the eye found on the page, and one click from deleting the board. It is an
 * admin's rare, destructive act, so it goes where rare acts go — still one
 * obvious place to find it, no longer in the way of everything else.
 *
 * Positioned under its trigger rather than portalled: the title row is not in
 * a scrolling pane, so there is nothing for it to drift away from.
 */
import { useEffect, useRef, useState } from 'react';
import AdminPill from '../common/AdminPill';

export interface RoomMenuItem {
  key: string;
  label: string;
  onSelect: () => void;
  /** Destructive: drawn in red. */
  danger?: boolean;
  disabled?: boolean;
  /** Why it is disabled, or what it does. */
  title?: string;
  testId?: string;
  /** Only admins see it: marked with the amber Admin pill. */
  admin?: boolean;
}

export default function RoomMenu({ items }: { items: RoomMenuItem[] }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  // Nothing to put behind it, no door: an empty menu is a button that lies.
  if (items.length === 0) return null;

  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More room actions"
        title="More room actions"
        className="px-2 py-0.5 rounded-md border border-gray-200 dark:border-gray-700 text-xs leading-none text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
        data-testid="room-menu"
      >
        <span aria-hidden className="text-sm leading-none">⋯</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-1 min-w-[11rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
        >
          {items.map(item => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              title={item.title}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              className={`block w-full px-3 py-1.5 text-left text-xs disabled:cursor-not-allowed disabled:opacity-40 ${
                item.danger
                  ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                  : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700'
              }`}
              data-testid={item.testId}
            >
              <span className="flex items-center gap-2">
                <span className="flex-1">{item.label}</span>
                {item.admin && <AdminPill className="text-[10px]" />}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
